import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { initGA4 } from "../../utils/analytics";

const COOKIE_CONSENT_KEY = "aepl_cookie_consent";

/**
 * CookieConsent — GDPR-compliant cookie consent banner.
 *
 * Stores user choice in localStorage:
 *  - "accepted" — all cookies allowed
 *  - "rejected" — only essential cookies
 *
 * Appears on first visit and stays dismissed until localStorage is cleared.
 */
const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent === "accepted") {
      // User already accepted — initialize GA4 immediately
      initGA4();
    } else if (!consent) {
      // First visit — show banner after a 2s delay
      const timer = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(timer);
    }
    // If "rejected", do nothing — no banner, no GA4
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    initGA4();
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "rejected");
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 pointer-events-none"
          role="dialog"
          aria-label="Cookie consent"
          aria-describedby="cookie-description"
        >
          <div className="max-w-[640px] mx-auto md:mx-0 md:ml-6 pointer-events-auto">
            <div
              className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/30"
              style={{
                background:
                  "linear-gradient(135deg, rgba(15, 23, 42, 0.97) 0%, rgba(30, 41, 59, 0.97) 100%)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
              }}
            >
              {/* Subtle top border glow */}
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-blue-400/40 to-transparent" />

              <div className="p-5 md:p-6">
                {/* Header Row */}
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
                      <Cookie size={18} className="text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-[15px] leading-tight">
                        Cookie Preferences
                      </h3>
                      <p className="text-slate-400 text-[12px] mt-0.5">
                        Your privacy matters to us
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleReject}
                    className="text-slate-500 hover:text-slate-300 transition-colors p-1 -mr-1 -mt-1 cursor-pointer"
                    aria-label="Dismiss cookie banner"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Description */}
                <p
                  id="cookie-description"
                  className="text-slate-400 text-[13px] leading-relaxed mb-4"
                >
                  We use cookies to enhance your browsing experience, analyse
                  site traffic, and understand how you interact with our
                  platform.{" "}
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="text-blue-400 hover:text-blue-300 transition-colors underline underline-offset-2 cursor-pointer"
                  >
                    {isExpanded ? "Show less" : "Learn more"}
                  </button>
                </p>

                {/* Expandable Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="pb-4 space-y-3">
                        {/* Essential */}
                        <div className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                          <Shield
                            size={16}
                            className="text-emerald-400 mt-0.5 shrink-0"
                          />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-white text-[13px] font-medium">
                                Essential
                              </span>
                              <span className="text-[10px] text-emerald-400 font-bold tracking-wide uppercase px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20">
                                Always active
                              </span>
                            </div>
                            <p className="text-slate-500 text-[12px] mt-1 leading-relaxed">
                              Required for core functionality. These cookies
                              remember your preferences and enable basic site
                              navigation.
                            </p>
                          </div>
                        </div>

                        {/* Analytics */}
                        <div className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                          <Cookie
                            size={16}
                            className="text-blue-400 mt-0.5 shrink-0"
                          />
                          <div>
                            <span className="text-white text-[13px] font-medium">
                              Analytics
                            </span>
                            <p className="text-slate-500 text-[12px] mt-1 leading-relaxed">
                              Help us understand how visitors interact with our
                              website by collecting and reporting information
                              anonymously.
                            </p>
                          </div>
                        </div>

                        <p className="text-slate-500 text-[12px]">
                          Read our{" "}
                          <Link
                            to="/privacy-policy"
                            className="text-blue-400 hover:text-blue-300 underline underline-offset-2 transition-colors"
                          >
                            Privacy Policy
                          </Link>{" "}
                          for full details.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleAccept}
                    className="flex-1 px-5 py-2.5 bg-blue-500 hover:bg-blue-400 text-white font-semibold text-[13px] rounded-lg transition-all duration-200 cursor-pointer shadow-lg shadow-blue-500/20 hover:shadow-blue-400/30"
                    aria-label="Accept all cookies"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleReject}
                    className="flex-1 px-5 py-2.5 bg-white/[0.06] hover:bg-white/[0.1] text-slate-300 font-semibold text-[13px] rounded-lg border border-white/[0.08] hover:border-white/[0.15] transition-all duration-200 cursor-pointer"
                    aria-label="Reject optional cookies, keep essential only"
                  >
                    Essential Only
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
